import Vue from 'vue';
import ShadowDom from '../shadow-dom/ShadowDom';

/**
 * Handles nested child components which will be rendered within the slot of a parent component.
 * Via cleanUp() this detects if a child component is no longer valid because it was removed from
 * the dom or because the slot was re-rendered and so all child components have been re-created.
 */
export default class ChildComponentCollection<T extends Vue> {
  items: Array<T> = [];

  get length (): number {
    return this.items.length;
  }

  push (...items: T[]): number {
    return this.items.push(...items);
  }

  get (index: number): T | null {
    if (index >= 0 && index < this.items.length) {
      return this.items[index];
    }
    return null;
  }

  first (): T | null {
    if (this.items.length > 0) {
      return this.items[0];
    }
    return null;
  }

  last (): T | null {
    if (this.items.length > 0) {
      return this.items[this.items.length - 1];
    }
    return null;
  }

  cleanUp (): Vue[] {
    const removedItems: Vue[] = [];
    this.items.forEach((item: T, index) => {
      if (!ShadowDom.isInDocument(item.$el)) {
        this.items.splice(index);
        removedItems.push(item);
      }
    });
    return removedItems;
  }

  toArray (): Array<T> {
    return this.items;
  }

  filter<S extends T> (callbackfn: (value: T, index: number, array: T[]) => boolean, thisArg?: any): T[] {
    return this.items.filter(callbackfn, thisArg);
  }

  forEach (callbackfn: (value: T, index: number, array: T[]) => void, thisArg?: any) {
    this.items.forEach(callbackfn, thisArg);
  }
}
